import React from "react";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { Skeleton } from "../dashboard/DashboardWidgets";

/**
 * The admin data table. Imported by every management page, so its props and the
 * column contract are load-bearing and unchanged:
 *
 *   data       - array of rows
 *   columns    - [{ id, label, render? }]  (render(row) overrides row[id])
 *   getPage    - the useFetch handle; .loading drives the skeleton, .reFetch on paging
 *   pageShow   - pass false to hide the pager
 *   totalCount - server total, /10 per page
 *   page       - current 1-based page
 *   setPage    - URLSearchParams setter
 *
 * What changed is only presentation. The old version drew a heavy
 * `border-2 border-rule` grid on every cell with a `bg-primary/10` header, and
 * swapped the whole table for a centred spinner while loading - which threw the
 * column widths away and made the page jump. Now: a single bordered surface,
 * hairline row separators, a sunken header, row hover, skeleton rows that keep
 * the layout stable, and a real empty state instead of a blank <tbody>.
 */
const ROWS_PER_PAGE = 10;
const EMPTY_ROWS = 8;

function TableFrame({ children }) {
    return (
        <div className="overflow-x-auto rounded-panel border border-rule-subtle bg-surface shadow-card">
            <table className="w-full min-w-[52rem] border-collapse text-center">
                {children}
            </table>
        </div>
    );
}

function HeaderRow({ columns }) {
    return (
        <thead>
            <tr className="border-b border-rule-subtle bg-surface-sunken">
                {columns.map((column) => (
                    <th
                        key={column.id}
                        scope="col"
                        className="whitespace-nowrap px-md py-md text-sm font-semibold text-ink-muted"
                    >
                        {column.label}
                    </th>
                ))}
            </tr>
        </thead>
    );
}

const ShareTable = ({
    data,
    columns,
    getPage,
    pageShow,
    totalCount,
    page,
    setPage,
}) => {
    const pageCount = Math.max(Math.ceil((totalCount ?? 0) / ROWS_PER_PAGE), 1);
    const currentPage = page || 1;

    const handlePrevPage = () => {
        setPage((prev) => {
            const next = new URLSearchParams(prev);
            next.set("page", currentPage - 1);
            return next;
        });
        getPage.reFetch();
    };

    const handleNextPage = () => {
        setPage((prev) => {
            const next = new URLSearchParams(prev);
            next.set("page", !page ? 2 : currentPage + 1);
            return next;
        });
        getPage.reFetch();
    };

    if (getPage.loading) {
        return (
            <TableFrame>
                <HeaderRow columns={columns} />
                <tbody>
                    {Array.from({ length: EMPTY_ROWS }).map((_, rowIndex) => (
                        <tr
                            key={rowIndex}
                            className="border-b border-rule-subtle last:border-b-0"
                        >
                            {columns.map((column) => (
                                <td key={column.id} className="px-md py-md">
                                    <Skeleton className="mx-auto h-[1.6rem] w-[70%]" />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </TableFrame>
        );
    }

    if (!data?.length) {
        return (
            <div className="flex flex-col items-center gap-xs rounded-panel border border-dashed border-rule bg-surface px-md py-4xl text-center">
                <span className="flex h-[4.8rem] w-[4.8rem] items-center justify-center rounded-pill bg-surface-sunken text-ink-subtle">
                    <InboxOutlinedIcon sx={{ fontSize: "2.4rem" }} />
                </span>
                <p className="text-base font-medium text-ink">موردی یافت نشد</p>
                <p className="max-w-[36rem] text-sm text-ink-subtle">
                    هنوز داده‌ای برای نمایش نیست، یا جستجوی شما نتیجه‌ای نداشت.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-md">
            <TableFrame>
                <HeaderRow columns={columns} />
                <tbody>
                    {data.map((row, rowIndex) => (
                        <tr
                            key={row?.id ?? rowIndex}
                            className="border-b border-rule-subtle text-ink transition-colors duration-150 last:border-b-0 hover:bg-surface-sunken"
                        >
                            {columns.map((column) => (
                                <td
                                    key={column.id}
                                    className="px-md py-md text-sm"
                                >
                                    {column.render
                                        ? column.render(row)
                                        : row[column.id]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </TableFrame>

            {pageShow !== false && (
                <div className="flex items-center justify-end gap-sm">
                    <span className="text-sm tabular-nums text-ink-subtle">
                        صفحه {currentPage.toLocaleString("fa-IR")} از{" "}
                        {pageCount.toLocaleString("fa-IR")}
                    </span>
                    <div className="flex items-center gap-2xs">
                        <button
                            type="button"
                            onClick={handlePrevPage}
                            disabled={currentPage <= 1}
                            aria-label="صفحه قبل"
                            className="flex h-[3.6rem] w-[3.6rem] items-center justify-center rounded-soft border border-rule-subtle text-ink-muted transition-colors duration-150 hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transition-none"
                        >
                            <NavigateNextIcon sx={{ fontSize: "2rem" }} />
                        </button>
                        <button
                            type="button"
                            onClick={handleNextPage}
                            disabled={currentPage >= pageCount}
                            aria-label="صفحه بعد"
                            className="flex h-[3.6rem] w-[3.6rem] items-center justify-center rounded-soft border border-rule-subtle text-ink-muted transition-colors duration-150 hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transition-none"
                        >
                            <NavigateBeforeIcon sx={{ fontSize: "2rem" }} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShareTable;
